/**
 * Which pick a keeper may be paid with, and what to do when the answer is "none".
 *
 * ONE RULE UNDERNEATH ALL OF IT — 2015-III-7. A keeper costs the round the Grid says, and he may
 * be charged to any pick the GM owns AT OR BEFORE that round. Moving him earlier is a penalty the
 * rules impose, so it is always allowed; moving him later would be paying less than the Grid says
 * and never is. Everything here is that one inequality applied in different directions.
 *
 * WHY IT IS ITS OWN MODULE. Three screens need the same answer and were about to guess at it
 * separately: the board (drag a keeper onto another slot), the mock trade tool (trade away the
 * pick a keeper is standing on), and the declaration screen (which already had `assignKeeperPicks`
 * for the simple case). Three implementations of a rule is three chances to disagree with the
 * rulebook, and the one that disagrees quietly is the one that costs somebody a keeper in August.
 *
 * WHAT `assignKeeperPicks` DOES NOT DO, and why this exists alongside it: it reports a failure
 * per keeper — "there is no pick at or before round 4 left to pay with" — which tells you a
 * symptom rather than the cause. When a GM trades away three picks, the keeper that fails is
 * whichever one the loop reached last, and the useful sentence is not about him. It is "you are
 * one pick short at round 4 or earlier". That is a property of the whole slate, so it is computed
 * over the whole slate here.
 */

export interface SlotKeeper {
  player: string;
  /** The round the Grid charges for him. */
  costRound: number;
}

/**
 * Where the slate runs out of picks, stated as the constraint rather than as a casualty.
 *
 * `round` is the deepest round that still helps: a pick in this round or earlier fixes it. `need`
 * is how many such picks are missing. Both are what a manager can act on — "retain your 4th, or
 * get one back at 4 or earlier" — where a per-keeper error is not.
 */
export interface Shortfall {
  round: number;
  need: number;
}

/**
 * Is this slate payable at all, and if not, where does it break?
 *
 * HALL'S CONDITION, in the only form that matters here. Keepers may only be paid with picks at or
 * before their cost round, so for every threshold r the slate needs at least as many owned picks
 * at or before r as it has keepers costing r or less. The FIRST threshold that fails is the one
 * to report: fixing it may or may not fix later ones, and telling somebody about the deepest
 * failure when a shallower one exists sends him to acquire the wrong pick.
 */
export function feasibility(
  keepers: readonly SlotKeeper[], ownedRounds: readonly number[],
): Shortfall | null {
  const owned = [...ownedRounds].sort((a, b) => a - b);
  const costs = keepers.map((k) => k.costRound).sort((a, b) => a - b);

  for (let i = 0; i < costs.length; i += 1) {
    const r = costs[i]!;
    // How many keepers must be paid at or before r — costs is sorted, so it is the run length.
    let demand = 0;
    while (demand < costs.length && costs[demand]! <= r) demand += 1;
    const supply = owned.filter((x) => x <= r).length;
    if (supply < demand) return { round: r, need: demand - supply };
  }
  return null;
}

export interface Fit {
  /** player -> the round he is charged to. */
  placed: Map<string, number>;
  /**
   * Keepers with nowhere legal to go. A payable slate can still strand somebody once `pinned` is
   * involved — pin the cheap keeper onto an early pick and the expensive one has nothing left —
   * so this is NOT the same question as `shortfall`, which asks whether the slate fits at all.
   */
  unplaced: string[];
  shortfall: Shortfall | null;
}

/**
 * Place every keeper on the LATEST legal pick, which is the cheapest one.
 *
 * Latest rather than earliest, and that is the whole point of the direction: a keeper charged to
 * a pick earlier than his cost burns draft capital the rules never asked for.
 *
 * Expensive keepers are placed first because they have the fewest legal homes. This does not
 * change how MANY keepers fit — with every range a prefix of the board, latest-fit places the
 * same number in any order — it changes WHICH one is left standing when one has to be. Cheap
 * first would strand the round-1 keeper to save the round-10 one, and it is the round-1 keeper
 * who is the better player.
 *
 * `pinned` holds placements the caller has already decided — a keeper the manager dragged
 * somewhere specific. A pin outside its keeper's legal range is refused rather than honoured.
 */
export function fitKeepers(
  keepers: readonly SlotKeeper[],
  ownedRounds: readonly number[],
  pinned: Readonly<Record<string, number>> = {},
  /**
   * An extra ceiling per keeper, tighter than his cost round.
   *
   * WHAT IT IS FOR: "resolve this without moving anybody LATER". A keeper may legally be charged
   * to any pick at or before his cost, which means a re-fit is free to slide somebody from his
   * 11th to his 12th — cheaper, legal, and not what the manager asked for when he dragged one
   * cell. Passing each keeper's current round as his ceiling keeps every move in one direction.
   */
  ceiling: Readonly<Record<string, number>> = {},
): Fit {
  const capOf = (k: SlotKeeper) => Math.min(k.costRound, ceiling[k.player] ?? Infinity);
  const placed = new Map<string, number>();
  const unplaced: string[] = [];
  const pool = [...ownedRounds].sort((a, b) => b - a);

  const take = (round: number): boolean => {
    const i = pool.indexOf(round);
    if (i < 0) return false;
    pool.splice(i, 1);
    return true;
  };

  for (const k of keepers) {
    const want = pinned[k.player];
    if (want === undefined) continue;
    if (want > capOf(k) || !take(want)) { unplaced.push(k.player); continue; }
    placed.set(k.player, want);
  }

  /* Ties break on the name, for the reason `assignKeeperPicks` states at length: without it the
     loser of a same-round collision is whichever one the caller happened to list second, and the
     two screens reading this module list them differently. The drift guard in
     `tests/keeperSlots.test.ts` only holds if both resolvers break the tie the same way. */
  for (const k of [...keepers]
    .sort((a, b) => capOf(a) - capOf(b) || a.player.localeCompare(b.player))) {
    if (placed.has(k.player) || unplaced.includes(k.player)) continue;
    const cap = capOf(k);
    const round = pool.find((r) => r <= cap);
    if (round === undefined) { unplaced.push(k.player); continue; }
    take(round);
    placed.set(k.player, round);
  }

  return { placed, unplaced, shortfall: feasibility(keepers, ownedRounds) };
}

/* -- moving one by hand ------------------------------------------------------------------------ */

export type MoveResult =
  /** Straight into an empty legal slot. */
  | { kind: 'moved'; placed: Map<string, number> }
  /** Two keepers exchanged slots, both legally. */
  | { kind: 'swapped'; placed: Map<string, number>; other: string }
  /** The slot is not one he could ever be charged to. */
  | { kind: 'illegal'; why: string }
  /**
   * Somebody is there and the straight swap is not legal for him. The caller must choose, which
   * is why both outcomes are computed here rather than described: the dialog can show what each
   * one actually does before anybody commits to it.
   */
  | {
    kind: 'blocked';
    other: string;
    why: string;
    /** Drop the occupant off the slate entirely. */
    ifCleared: Fit;
    /** Keep everybody and re-fit freely — cheapest legal pick for each, in either direction. */
    ifEscalated: Fit | null;
    /**
     * Keep everybody, and let nobody drift LATER than where he already sits.
     *
     * A separate answer because the free re-fit will happily hand a keeper a later, cheaper pick
     * to solve the puzzle, and a manager who dragged one cell did not ask for his 11th to become
     * a 12th. Null when the constraint makes it impossible — which is often, and correctly so:
     * it is a strictly harder problem than `ifEscalated`.
     */
    ifBumped: Fit | null;
  };

/**
 * Drag `player` onto `toRound`.
 *
 * The order of the branches is the order a person expects: nonsense first, then the easy case,
 * then the swap, then the choice. A swap is tried before the dialog because two keepers trading
 * slots is overwhelmingly what a drag onto an occupied cell means, and asking about it when it is
 * simply legal would be the app being pedantic at somebody mid-draft.
 */
export function moveKeeper(
  keepers: readonly SlotKeeper[],
  ownedRounds: readonly number[],
  current: ReadonlyMap<string, number>,
  player: string,
  toRound: number,
): MoveResult {
  const me = keepers.find((k) => k.player === player);
  if (!me) return { kind: 'illegal', why: `${player} is not on this slate.` };

  if (!ownedRounds.includes(toRound)) {
    return { kind: 'illegal', why: `You do not own a Round ${toRound} pick.` };
  }
  if (toRound > me.costRound) {
    return {
      kind: 'illegal',
      why: `${player} costs a Round ${me.costRound} pick. A keeper can be moved to an earlier `
        + 'round, never a later one — 2015-III-7.',
    };
  }

  const occupantEntry = [...current].find(([p, r]) => r === toRound && p !== player);

  /*
   * A GM can hold two picks in the same round, so "occupied" is about capacity rather than about
   * the number: if he owns two 9ths and only one keeper sits on a 9th, the drop is free.
   */
  const capacity = ownedRounds.filter((r) => r === toRound).length;
  const used = [...current].filter(([p, r]) => r === toRound && p !== player).length;
  /*
   * ONLY THE MAN BEING MOVED IS PINNED. Everybody else re-fits to his cheapest legal pick.
   *
   * Pinning the whole team where it currently stands looks conservative and is not: a keeper who
   * was pushed onto an earlier pick to make room STAYS there once the keeper he made room for
   * has gone somewhere else. Move a man onto the 2nd and the 2nd's occupant escalates to the
   * 1st; move him back off it and the occupant should get his 2nd back, because an early pick
   * spent for no reason is a pick thrown away. Re-fitting is the same rule the board applies
   * everywhere else — latest legal pick, which is the cheapest one.
   */
  if (used < capacity) {
    return {
      kind: 'moved',
      placed: fitKeepers(keepers, ownedRounds, { [player]: toRound }).placed,
    };
  }

  const [other] = occupantEntry!;
  const otherK = keepers.find((k) => k.player === other)!;
  const from = current.get(player);

  if (from !== undefined && from <= otherK.costRound) {
    // Both ends of the swap are pinned — that is what a swap means — and nobody else is.
    return {
      kind: 'swapped',
      placed: fitKeepers(keepers, ownedRounds, { [player]: toRound, [other]: from }).placed,
      other,
    };
  }

  const withoutOther = keepers.filter((k) => k.player !== other);
  const ifCleared = fitKeepers(withoutOther, ownedRounds, { [player]: toRound });
  const escalated = fitKeepers(keepers, ownedRounds, { [player]: toRound });
  const ceiling: Record<string, number> = {};
  for (const [p, r] of current) if (p !== player) ceiling[p] = r;
  const bumped = fitKeepers(keepers, ownedRounds, { [player]: toRound }, ceiling);
  return {
    kind: 'blocked',
    other,
    why: from === undefined
      ? `${other} is on Round ${toRound} and ${player} is not charged anywhere yet.`
      : `${other} costs a Round ${otherK.costRound} pick, so he cannot take `
        + `${player}'s Round ${from} — that would be paying less than the Grid says.`,
    ifCleared,
    ifEscalated: escalated.unplaced.length === 0 ? escalated : null,
    /* Only worth offering when it differs from the free re-fit; otherwise it is the same button
       twice with two names on it. */
    ifBumped: bumped.unplaced.length === 0
      && !samePlacement(bumped.placed, escalated.placed) ? bumped : null,
  };
}

function samePlacement(a: ReadonlyMap<string, number>, b: ReadonlyMap<string, number>): boolean {
  if (a.size !== b.size) return false;
  for (const [k, v] of a) if (b.get(k) !== v) return false;
  return true;
}

/** One sentence a manager can act on, or null when the slate fits. */
export function shortfallSays(s: Shortfall | null): string | null {
  if (!s) return null;
  const picks = s.need === 1 ? 'a pick' : `${s.need} picks`;
  return `This leaves the slate ${s.need === 1 ? 'one pick' : `${s.need} picks`} short: `
    + `${picks} in Round ${s.round} or earlier has to be kept back, or come from the other side.`;
}
